import * as ng from 'angular';
import * as uir from '@uirouter/angularjs';

import {SessionService, L10nService, FontService} from '../../shared';

export class BrandEditController {
   static $inject = [
      '$state',
      '$stateParams',
      '$window',
      'l10n',
      'session',
      'brandsApi',
      'dialogs',
      'toast',
      'loader',
      'titleService',
      'fonts',
      'tinymceOptions'
   ];

   constructor(
      private $state: uir.StateService,
      $stateParams: uir.StateParams,
      private $window: ng.IWindowService,
      l10n: L10nService,
      private session: SessionService,
      private brandsApi,
      private dialogs,
      private toast,
      private loader,
      titleService,
      fonts: FontService,
      public tinymceOptions
   ) {
      titleService.setTitle('Brand Details');

      this.isCreate = $state.current.name == 'addBrand';

      if (this.isCreate) {
         this.brand = {};
         this.model = {
            settings: {
               anonymization: {}
            }
          };
      } else {
         let brandToEdit = $stateParams.id ? this.session.getBrand($stateParams.id) : this.session.getBrand();
         this.model = ng.copy(brandToEdit);
         this.brand = brandToEdit;

         if (this.model.settings.anonymization != null) {
            this.enableAnonymizationInviteeProspects = this.model.settings.anonymization.anonDaysInviteeProspects > 0;
            this.enableAnonymizationPreRegParticipants = this.model.settings.anonymization.anonDaysPreRegParticipants > 0;
         } else {
            // set defaults
            this.model.settings.anonymization = {};
         }
      }

      this.org = $stateParams.org ? $stateParams.org : this.session.getOrg();

      this.languages = l10n.getLanguageCodes();

      this.fonts = fonts.fonts;
      this.emailFonts = fonts.pcFonts;
      this.appFonts = fonts.appFonts;

      if (!this.model.settings.experience) {
         this.model.settings.experience = {
            appFont: 'Helvetica',
            backgroundColor: '#FFFFFF',
            buttonColor: '#5A9361',
            buttonColorUnselected: '#FFFFFF',
            buttonTextColor: '#FFFFFF',
            buttonTextColorUnselected: '#ACACAC',
            emailFont: 'Arial',
            fontColor: '#738891',
            fontSize: 16,
            footerBackgroundColor: '#E4E4E4',
            footerFontColor: '#444444',
            footerFontSize: 14,
            footerHideDefaultLinks: false,
            footerLinkColor: '#5A9361',
            footerLinkHoverColor: '#2E5261',
            footerText: {},
            headerFontColor: '#2E5261',
            headerFontSize: 32,
            linkColor: '#00689D',
            linkHoverColor: '#2E5261',
            mediaFont: 'Open Sans',
            socialLinkColor: '#5A9361',
            socialLinkHoverColor: '#2E5261',
            uploads: {}
         };
      }

      // bc fix, remove unused setting
      delete this.model.settings.experience.font;
   }

   readonly eventFields = [
      'LocationCode',
      'EventCode',
      'ModuleId',
      'EventTypeId',
      'PartnerId',
      'RegionId',
      'VendorNameId',
      'VendorContactName',
      'VendorContactPhone',
      'VendorContactEmail',
      'ProductSpecialists',
      'Category',
      'SubCategory',
      'DepartmentId',
      'ActivationTypes'
   ];

   readonly participantFields = [
      'FirstName',
      'LastName',
      'Email',
      'PhoneHome',
      'PhoneMobile',
      'Address',
      'City',
      'State',
      'PostalCode',
      'Country'
   ];

   isCreate: boolean;
   model;

   readonly org;
   readonly brand;

   language = 'en';
   readonly languages: string[];

   readonly fonts: string[];
   readonly emailFonts: string[];
   readonly appFonts: string[];

   enableAnonymizationInviteeProspects:boolean;
   enableAnonymizationPreRegParticipants:boolean;

   create(params) {
      this.brandsApi
         .create(params)
         .then((brand) => {
            return this.session.refreshUser({orgId: this.org.id, brandId: brand.id});
         })
         .then(() => {
            this.toast
               .success()
               .title('Brand created successfully')
               .message('You can now add a new campaign under this brand')
               .send();

            this.$state.go('campaign.add');
         })
         .finally(() => this.loader.hide());
   }

   del() {
      this.dialogs.confirm('Are you sure you want to delete this brand?').then(() => {
         this.brandsApi
            .del(this.model.id)
            .then(() => {
               this.toast.message(`Brand "${this.model.name}" was deleted`).send();
               return this.session.refreshUser({orgId: this.org.id});
            })
            .then(() => this.$state.reload())
            .catch((message) => {
               let msg = message.data ? message.data : 'Internal error, please file a bug report';
               this.dialogs.alert(`Could not delete ${this.model.name}. Reason: ${msg}`);
            })
            .finally(() => this.loader.hide());
      });
   }

   goBack() {
      this.$window.history.back();
   }

   nullifyEmptyStrings() {
      const fieldsToNullify = [this.model.settings.experience.contactOptin, 
         this.model.settings.experience.contestOptin,
         this.model.settings.experience.promoOptin,
         this.model.settings.experience.salesOptin,
         this.model.settings.experience.termsAgree,
         this.model.settings.experience.termsURL,
         this.model.settings.experience.privacyURL,
         this.model.settings.experience.cookiesURL
      ];
      for (let field of fieldsToNullify) {
         for (let lang in field) {
            if (field[lang] == '' || field[lang] == null) {
               delete field[lang]
            }
         }
      }
   }

   save() {
      let params = this.model;
      this.loader.showSpinner();
      this.nullifyEmptyStrings();
      if (this.isCreate) {
         //set org id for new brand
         params.orgId = this.org.id;
         this.create(params);
      } else {
         let promptForPropagation = false;
         //prompt only if existing campaigns exist and brand settings have changed
         if (this.brand.campaigns != null && this.brand.campaigns.length > 0) {
            const brandAnonymization = this.brand.settings.anonymization;
            const paramsAnonymization = params.settings.anonymization;

            if (brandAnonymization?.anonAfterEventEnd !== paramsAnonymization?.anonAfterEventEnd ||
               brandAnonymization?.anonOmitOptins !== paramsAnonymization?.anonOmitOptins ||
               brandAnonymization?.anonDaysInviteeProspects !== paramsAnonymization?.anonDaysInviteeProspects ||
               brandAnonymization?.anonDaysPreRegParticipants !== paramsAnonymization?.anonDaysPreRegParticipants) {
               promptForPropagation = true;
            }
         }

         if (promptForPropagation) {
            params.settings.anonPropagateToCampaigns = false;
            const message = 'You have updated this brand\'s anonymization settings. '
               +'This will change the default setting for any new campaigns you create for this brand. '
               +'\n\n'
               +'Do you also wish to update anonymization settings for all existing campaigns for this brand?';
            this.dialogs
               .yesno(
                  message
               )
               .then((value) => {
                  if (value == 'yes' || value == 'no') {
                     if (value == 'yes') {
                        params.settings.anonPropagateToCampaigns = true;
                     }
                     this.update(params);
                  }
               })
               .catch((ex) => {
                  console.debug('ex = ' + ex)
               })
               .finally(() => {});
         } else {
            this.update(params);
         }
      }
   }

   update(params) {
      this.brandsApi
         .update(params)
         .then((brand) => {
            return this.session.refreshUser({orgId: this.org.id, brandId: brand.id});
         })
         .then(() => {
            this.model = ng.copy(this.session.getBrand());
            this.toast.success().message('Brand updated successfully').send();
         })
         //.then(() => this.$state.reload())
         .catch((message) => {
            let msg = message.data ? message.data : 'Internal error, please file a bug report';
            this.dialogs.alert(`Could not update anonymization settings ${this.model.name}. Reason: ${msg}`);
         })
         .finally(() => {
            this.loader.hide();
            this.$state.reload();
         });
   }

   toggleAnonymizationInviteeProspects() {
      if (this.enableAnonymizationInviteeProspects) {
         this.model.settings.anonymization.anonDaysInviteeProspects = this.brand.settings?.anonymization?.anonDaysInviteeProspects ?? 30;
      } else {
         this.model.settings.anonymization.anonDaysInviteeProspects = null;
      }
   }

   toggleAnonymizatioPreRegParticipants() {
      if (this.enableAnonymizationPreRegParticipants) {
         this.model.settings.anonymization.anonDaysPreRegParticipants = this.brand.settings?.anonymization?.anonDaysPreRegParticipants ?? 30;
         this.model.settings.anonymization.anonAfterEventEnd = true;
      } else {
         this.model.settings.anonymization.anonOmitOptins = false;
         this.model.settings.anonymization.anonDaysPreRegParticipants = null;
         this.model.settings.anonymization.anonAfterEventEnd = null;
      }
   }
}

ng.module('e360').controller('BrandEditController', BrandEditController);
